import { useCallback, useEffect, useRef, useState } from 'react';

import type { FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT } from '../analytics.ts';
import type { PublishedSuggestionsPanelActions } from '../published/usePublishedSuggestionsPanel.ts';

import {
	cancelLiveDocEditModeSelection,
	selectSuggestionInLiveDocEditMode,
} from './liveDocEditModeSelection.ts';
import { useIsLiveDocViewMode } from './useIsLiveDocViewMode.ts';

/**
 * Panel actions for a live doc in view mode. Suggestions are acted on in edit mode, which on a live
 * doc is a mode switch on the same editor.
 */
export const useLiveDocViewSuggestionsPanel = (
	editorAPI: FullPageEditorPresetAPI | undefined,
	switchToEditMode: (() => void) | undefined,
): PublishedSuggestionsPanelActions => {
	const [isHeadingToEditMode, setIsHeadingToEditMode] = useState(false);
	const isLiveDocViewMode = useIsLiveDocViewMode();
	const [wasLiveDocViewMode, setWasLiveDocViewMode] = useState(isLiveDocViewMode);
	if (wasLiveDocViewMode !== isLiveDocViewMode) {
		setWasLiveDocViewMode(isLiveDocViewMode);
		setIsHeadingToEditMode(false);
	}

	// Back in view mode before the editor reached edit mode, so the viewed suggestion is stale.
	const wasLiveDocViewModeRef = useRef(isLiveDocViewMode);
	useEffect(() => {
		if (isLiveDocViewMode && !wasLiveDocViewModeRef.current) {
			cancelLiveDocEditModeSelection();
		}
		wasLiveDocViewModeRef.current = isLiveDocViewMode;
	}, [isLiveDocViewMode]);

	const openInEditMode = useCallback(
		(suggestionId?: string) => {
			if (
				suggestionId &&
				switchToEditMode &&
				selectSuggestionInLiveDocEditMode(editorAPI, suggestionId)
			) {
				setIsHeadingToEditMode(true);
			}
			switchToEditMode?.();
		},
		[editorAPI, switchToEditMode],
	);

	const startReviewInEditMode = useCallback(() => {
		switchToEditMode?.();
		editorAPI?.aiSuggestions?.actions.setThinkingState('thinking', {
			entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
		});
	}, [editorAPI, switchToEditMode]);

	return { isHeadingToEditMode, openInEditMode, startReviewInEditMode };
};
